/**
 * ProposalSavingsTimeline — Cumulative savings chart
 *
 * Recharts AreaChart showing cumulative customer savings over contract term.
 * Includes lifetime savings callout.
 *
 * Compare mode: accepts `entries` array to overlay multiple scenario lines.
 */

import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { vortxColors, vortxTypography, chartColors, useThemeMode } from '@vortx/shared-ui';
import { formatCurrency } from './extractProposalData';

const compareColors = [chartColors.savings, chartColors.ev, chartColors.solar, chartColors.battery];

/** Build cumulative chart data from a single scenario's data object */
function buildChartData(data) {
  const { annualCashflows, mode } = data;

  if (annualCashflows && annualCashflows.length > 0) {
    let cumulative = 0;
    return annualCashflows.map((cf) => {
      cumulative += cf.netCashflow;
      return { year: cf.year, cumulative };
    });
  }

  if (mode === 'capex' && data.year1AnnualSavings && data.capexTerms?.term) {
    const term = data.capexTerms.term;
    const annualSav = data.year1AnnualSavings;
    const netCost = data.capexTerms.netCost || 0;
    let cumulative = -netCost;
    const points = [{ year: 0, cumulative }];
    for (let y = 1; y <= term; y++) {
      cumulative += annualSav;
      points.push({ year: y, cumulative });
    }
    return points;
  }

  return null;
}

export default function ProposalSavingsTimeline({ data, entries, headerExtra }) {
  const { isRetro } = useThemeMode();
  // Compare mode — build merged chart data with one cumulative column per scenario
  const compareChartData = useMemo(() => {
    if (!entries) return null;

    const seriesArrays = entries.map((e) => buildChartData(e.data));
    if (seriesArrays.every((s) => !s || s.length === 0)) return null;

    // Collect all years across scenarios
    const yearSet = new Set();
    seriesArrays.forEach((s) => {
      if (s) s.forEach((pt) => yearSet.add(pt.year));
    });
    const years = [...yearSet].sort((a, b) => a - b);

    // Merge into rows keyed by year
    return years.map((year) => {
      const row = { year };
      seriesArrays.forEach((s, i) => {
        const pt = s && s.find((p) => p.year === year);
        row[`cumulative_${i}`] = pt ? pt.cumulative : null;
      });
      return row;
    });
  }, [entries]);

  // Single scenario mode
  const singleChartData = useMemo(() => {
    if (entries || !data) return null;
    return buildChartData(data);
  }, [data, entries]);

  // Determine which mode we're in
  const isCompare = entries && compareChartData && compareChartData.length > 0;
  const isSingle = !entries && singleChartData && singleChartData.length > 0;

  if (!isCompare && !isSingle) return null;

  const chartDataFinal = isCompare ? compareChartData : singleChartData;
  const finalCumulative = isSingle
    ? (singleChartData[singleChartData.length - 1]?.cumulative ?? data?.lifetimeSavings)
    : null;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          flexWrap: 'wrap',
          gap: 1,
          mb: 2,
        }}
      >
        <Typography
          variant="overline"
          sx={{
            color: vortxColors.stone,
            fontWeight: 600,
            letterSpacing: '0.1em',
          }}
        >
          Savings Over Time
        </Typography>

        {headerExtra}

        {/* Single mode — lifetime savings figure */}
        {isSingle && finalCumulative != null && (
          <Box sx={{ textAlign: 'right' }}>
            <Typography
              variant="caption"
              sx={{ color: vortxColors.stone, display: 'block' }}
            >
              Lifetime Savings
            </Typography>
            <Typography
              sx={{
                fontFamily: vortxTypography.mono,
                fontWeight: 700,
                fontSize: '1.25rem',
                color: chartColors.savings,
              }}
            >
              {formatCurrency(finalCumulative)}
            </Typography>
          </Box>
        )}

        {/* Compare mode — color-coded legend */}
        {isCompare && (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {entries.map((entry, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: compareColors[i % compareColors.length],
                  flexShrink: 0,
                }} />
                <Typography variant="caption" sx={{
                  color: compareColors[i % compareColors.length],
                  fontFamily: vortxTypography.body,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                }}>
                  {entry.name}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Box
        sx={{
          width: '100%',
          height: 280,
          borderRadius: 2,
          border: `1px solid rgba(255,255,255,0.08)`,
          bgcolor: 'rgba(255,255,255,0.02)',
          p: 1,
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartDataFinal} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
            <defs>
              {isCompare ? (
                entries.map((_, i) => (
                  <linearGradient key={i} id={`savingsGradient-${i}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={compareColors[i % compareColors.length]} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={compareColors[i % compareColors.length]} stopOpacity={0.03} />
                  </linearGradient>
                ))
              ) : (
                <linearGradient id="savingsGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={chartColors.savings} stopOpacity={0.4} />
                  <stop offset="95%" stopColor={chartColors.savings} stopOpacity={0.05} />
                </linearGradient>
              )}
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="rgba(255,255,255,0.06)"
            />
            <XAxis
              dataKey="year"
              tick={{ fill: vortxColors.stone, fontSize: 12 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickLine={false}
              label={{
                value: 'Year',
                position: 'insideBottomRight',
                offset: -5,
                fill: vortxColors.stone,
                fontSize: 11,
              }}
            />
            <YAxis
              tick={{ fill: vortxColors.stone, fontSize: 12 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickLine={false}
              tickFormatter={(v) => {
                if (Math.abs(v) >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
                if (Math.abs(v) >= 1000) return `$${(v / 1000).toFixed(0)}K`;
                return `$${v}`;
              }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: isRetro ? '#242424' : '#fff',
                border: `1px solid ${vortxColors.teal}`,
                borderRadius: 6,
                fontFamily: vortxTypography.mono,
                fontSize: 13,
              }}
              formatter={(value, name) => {
                if (isCompare) {
                  const idx = parseInt(name.split('_')[1], 10);
                  return [formatCurrency(value), entries[idx]?.name || name];
                }
                return [formatCurrency(value), 'Cumulative Savings'];
              }}
              labelFormatter={(label) => `Year ${label}`}
            />
            {isCompare ? (
              entries.map((_, i) => (
                <Area
                  key={i}
                  type="monotone"
                  dataKey={`cumulative_${i}`}
                  stroke={compareColors[i % compareColors.length]}
                  strokeWidth={2}
                  fill={`url(#savingsGradient-${i})`}
                  connectNulls
                />
              ))
            ) : (
              <Area
                type="monotone"
                dataKey="cumulative"
                stroke={chartColors.savings}
                strokeWidth={2}
                fill="url(#savingsGradient)"
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
